/**
 * Multi-select applicant picker shared by the "request something from the
 * household" dialogs — Request Documents and Request Bank Account (client
 * 2026-08-27). One card per member: check circle, initials avatar, name and
 * role chip. Both dialogs fan their intent out over the selected ids, which
 * the server handlers loop, so the SAME picker serves the individual page
 * (household of one) and the combined page.
 */
import { Check } from 'lucide-react'
import { applicationTypeLabel } from '#app/utils/application-detail-v2/display'
import type {
	ApplicationSummary,
	GroupMember,
} from '#app/utils/application-detail-v2/types'
import { cn } from '#app/utils/misc'
import { PastelChip } from '../primitives'

export interface ApplicantOption {
	id: string
	name: string
	badge: string
	guarantor: boolean
	phone: string | null
}

/** Two-letter initials from a display name (first + last word). */
export function applicantInitials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean)
	if (parts.length === 0) return '?'
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/**
 * Household members as picker options; a solo application (no group) falls
 * back to the page's own applicant, so the picker always has something to
 * select.
 */
export function buildApplicantOptions(
	application: ApplicationSummary,
	groupMembers: GroupMember[],
): ApplicantOption[] {
	if (groupMembers.length > 0) {
		return groupMembers.map((member) => ({
			id: member.applicationId,
			name: member.displayName,
			badge: member.relationshipLabel,
			guarantor: member.applicationType === 'guarantor',
			phone: member.phone ?? null,
		}))
	}
	return [
		{
			id: application.id,
			name: application.applicantName,
			badge: applicationTypeLabel(application.applicationType),
			guarantor: application.applicationType === 'guarantor',
			phone: application.applicantPhone,
		},
	]
}

/** The dialogs' check circle — also used by the document chips. */
export function CheckCircle({ checked }: { checked: boolean }) {
	return (
		<span
			aria-hidden="true"
			className={cn(
				'flex size-6 shrink-0 items-center justify-center rounded-full border transition-colors',
				checked
					? 'border-[#32302F] bg-[#32302F] text-white'
					: 'border-[#E5E4E2] bg-white',
			)}
		>
			{checked ? <Check className="size-3.5" strokeWidth={3} /> : null}
		</span>
	)
}

export function ApplicantPicker({
	options,
	selectedIds,
	onToggle,
	disabled,
	className,
}: {
	options: ApplicantOption[]
	selectedIds: string[]
	onToggle: (id: string) => void
	disabled?: boolean
	className?: string
}) {
	return (
		<div className={cn('space-y-3', className)}>
			{options.map((option) => {
				const checked = selectedIds.includes(option.id)
				return (
					<button
						key={option.id}
						type="button"
						disabled={disabled}
						aria-pressed={checked}
						onClick={() => onToggle(option.id)}
						className={cn(
							'flex w-full cursor-pointer items-center gap-3 rounded-2xl border p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
							checked
								? 'border-[#32302F]'
								: 'border-[#F0F0F0] hover:border-[#D6D3D1]',
						)}
					>
						<CheckCircle checked={checked} />
						<span className="flex size-9 shrink-0 items-center justify-center rounded-full border-2 border-[#16A34A] text-[12px] font-semibold text-[#16A34A]">
							{applicantInitials(option.name)}
						</span>
						<span className="min-w-0 flex-1 truncate text-[16px] text-[#32302F]">
							{option.name}
						</span>
						<PastelChip
							tone={option.guarantor ? 'damber' : 'dgreen'}
							label={option.badge}
							className="px-3 py-1 text-[13px]"
						/>
					</button>
				)
			})}
		</div>
	)
}
